import { useState, useEffect } from "react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const TARGET_ZOOM = 75;
const ZOOM_TOLERANCE = 5; // Allow some tolerance (e.g., 70-80% is acceptable)

export default function BrowserZoomAlert() {
  const [currentZoom, setCurrentZoom] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const getZoomLevel = () => {
    // devicePixelRatio gives us the zoom level combined with display scaling
    // This is the most reliable cross-browser method
    return Math.round(window.devicePixelRatio * 100);
  };

  const isZoomOutOfRange = (zoom) => {
    return Math.abs(zoom - TARGET_ZOOM) > ZOOM_TOLERANCE;
  };

  useEffect(() => {
    // Initial check
    const initialZoom = getZoomLevel();
    setCurrentZoom(initialZoom);

    // Listen for zoom changes (resize event fires when zoom changes)
    const handleResize = () => {
      const newZoom = getZoomLevel();
      setCurrentZoom(newZoom);
      // Reset dismissed state when zoom changes so user sees alert again
      if (isZoomOutOfRange(newZoom)) {
        setIsDismissed(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Don't show if dismissed, zoom not detected, or zoom is within acceptable range
  if (isDismissed || currentZoom === null || !isZoomOutOfRange(currentZoom)) {
    return null;
  }

  return (
    <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700">
      <div className="px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <span className="font-medium">Browser zoom is at {currentZoom}%.</span>
            {" "}For consistent map exports, please adjust to{" "}
            <span className="font-semibold">{TARGET_ZOOM}%</span> using{" "}
            <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 dark:bg-amber-800 border border-amber-300 dark:border-amber-600 rounded">
              Ctrl
            </kbd>
            {" + "}
            <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 dark:bg-amber-800 border border-amber-300 dark:border-amber-600 rounded">
              -
            </kbd>
            {" or "}
            <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 dark:bg-amber-800 border border-amber-300 dark:border-amber-600 rounded">
              Ctrl
            </kbd>
            {" + "}
            <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 dark:bg-amber-800 border border-amber-300 dark:border-amber-600 rounded">
              +
            </kbd>
          </p>
        </div>
        <button
          onClick={() => setIsDismissed(true)}
          className="p-1 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-800 rounded transition-colors"
          aria-label="Dismiss zoom alert"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
